// The MIT License (MIT)
//
// Copyright (c) 2014-2025 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
import olInteractionInteraction from 'ol/interaction/Interaction';
import olLayerBase from 'ol/layer/Base';
import olLayerGroup from 'ol/layer/Group';
import olLayerLayer from 'ol/layer/Layer';
import olSourceImage from 'ol/source/Image';
import olSourceTile from 'ol/source/Tile';
/**
 * Provides a function that adds an "active" property (using
 * `Object.defineProperty`) to an interaction, making it possible to use ngModel
 * to activate/deactivate interactions.
 *
 * Example:
 *
 *      <input type="checkbox" ngModel="interaction.active" />
 *
 * @param {import('ol/interaction/Interaction').default} interaction Interaction to decorate.
 */
export function interactionDecoration(interaction) {
    console.assert(interaction instanceof olInteractionInteraction);
    Object.defineProperty(interaction, 'active', {
        get: () => interaction.getActive(),
        set: (val) => {
            interaction.setActive(val);
        },
    });
}
/**
 * Provides a function that adds properties (using
 * `Object.defineProperty`) to the layer, making it possible to control layer
 * properties with ngModel.
 *
 * Example:
 *
 *      <input type="checkbox" ngModel="layer.visible" />
 *
 * @param {import('ol/layer/Base').default} layer Layer to decorate.
 */
export function layerDecoration(layer) {
    console.assert(layer instanceof olLayerBase);
    Object.defineProperty(layer, 'visible', {
        configurable: true,
        /**
         * @returns {boolean} Visible.
         */
        get: () => layer.getVisible(),
        /**
         * @param {boolean} val Visible.
         */
        set: (val) => {
            layer.setVisible(val);
        },
    });
    Object.defineProperty(layer, 'opacity', {
        configurable: true,
        /**
         * @returns {number} Opacity.
         */
        get: () => layer.getOpacity(),
        /**
         * @param {number} val Opacity.
         */
        set: (val) => {
            layer.setOpacity(val);
        },
    });
}
/**
 * Provides a function that adds a 'loading 'property (using
 * `Object.defineProperty`) to an ol.layer.Group or a layer with
 * an ol.source.Tile or an ol.source.Image source.
 * This property is true when the layer is loading and false otherwise.
 *
 * Example:
 *
 *      <span ng-if="layer.loading">please wait</span>
 *
 * @param {import('ol/layer/Base').default} layer layer.
 * @param {angular.IScope} $scope Scope.
 */
export function layerLoading(layer, $scope) {
    let source;
    /**
     * @type {string[]}
     */
    let incrementEvents = [];
    /**
     * @type {string[]}
     */
    let decrementEvents = [];
    /**
     * @function
     * @private
     */
    const incrementLoadCount_ = increment_;
    /**
     * @function
     * @private
     */
    const decrementLoadCount_ = decrement_;
    layer.set('load_count', 0, true);
    if (layer instanceof olLayerGroup) {
        layer.getLayers().on(
        /** @type {import('ol/Observable').EventTypes} */ ('add'), 
        /** @type {function(?): ?} */ (
        /**
         * @param {import('ol/Collection').CollectionEvent} olEvent the progress event.
         */
        (olEvent) => {
            const newLayer = olEvent.element;
            newLayer.set('parent_group', layer);
        }));
    }
    if (layer instanceof olLayerLayer) {
        source = layer.getSource();
        if (source === null) {
            return;
        }
        else if (source instanceof olSourceTile) {
            incrementEvents = ['tileloadstart'];
            decrementEvents = ['tileloadend', 'tileloaderror'];
        }
        else if (source instanceof olSourceImage) {
            incrementEvents = ['imageloadstart'];
            decrementEvents = ['imageloadend', 'imageloaderror'];
        }
        else {
            console.assert(false, 'unsupported source type');
        }
        source.on(incrementEvents, () => {
            incrementLoadCount_(layer);
            $scope.$applyAsync();
        });
        source.on(decrementEvents, () => {
            decrementLoadCount_(layer);
            $scope.$applyAsync();
        });
    }
    Object.defineProperty(layer, 'loading', {
        configurable: true,
        get: () => /** @type {number} */ (layer.get('load_count')) > 0,
    });
    /**
     * @function
     * @param {import('ol/layer/Base').default} layer Layer
     * @private
     */
    function increment_(layer) {
        let load_count = /** @type {number} */ (layer.get('load_count'));
        /**
         * @type {import('ol/layer/Base').default}
         */
        const parent = layer.get('parent_group');
        layer.set('load_count', ++load_count, true);
        if (parent) {
            increment_(parent);
        }
    }
    /**
     * @function
     * @param {import('ol/layer/Base').default} layer Layer
     * @private
     */
    function decrement_(layer) {
        let load_count = /** @type {number} */ (layer.get('load_count'));
        /**
         * @type {import('ol/layer/Base').default}
         */
        const parent = layer.get('parent_group');
        layer.set('load_count', --load_count, true);
        if (parent) {
            decrement_(parent);
        }
    }
}
//# sourceMappingURL=decorate.js.map